import { useRef } from 'react'
import { motion } from 'framer-motion'
import { slugs } from '@/constants/slugs'
import { useIncrementer } from '@/services/GoalService/hooks/useIncrementer'
import { useOnScreen } from '@/utils/ScreenUtils'
import { useTranslate } from '@/utils/translate/translate-client'
import useTheatricalGoalService from '@/views/TicketCheckoutViews/hooks/useTheatricalGoalService'

interface DavidGoalProgressProps {
  releaseDate?: string
  // Styling props for different contexts
  textColor?: string
  progressBarBg?: string
  progressBarBorder?: string
  progressBarFillBg?: string
  textSize?: 'sm' | 'md' | 'lg'
  // Animation props
  shouldAnimateOnScroll?: boolean
  showGoalLineTitle?: boolean
}

export function DavidGoalProgress({
  releaseDate,
  textColor = '#411e09',
  progressBarBg = 'bg-white',
  progressBarBorder = 'border-[#f0e0d3]',
  progressBarFillBg = 'bg-[#ea6f13]',
  textSize = 'md',
  shouldAnimateOnScroll = false,
  showGoalLineTitle = true,
}: DavidGoalProgressProps) {
  // Fetch goal data directly in the component
  const { goalLineInfo } = useTheatricalGoalService({
    theatricalSlug: slugs.david,
    releaseDate: releaseDate || '',
    usePifGoal: true,
  })

  const { ticketsSold, milestonePercentage, currentMilestone, goalLineTitle, isLastMilestone } = goalLineInfo
  const { t } = useTranslate('pif')
  const sectionRef = useRef<HTMLDivElement>(null)
  const { isIntersecting } = useOnScreen(sectionRef)

  // Use the standard incrementer hook for animating numbers
  const incrementerNumber = useIncrementer(ticketsSold, 0.75)
  const shouldAnimate = shouldAnimateOnScroll ? isIntersecting : true
  const ticketCount = shouldAnimate ? Math.ceil(incrementerNumber) : 0
  const progressWidth = shouldAnimate ? (incrementerNumber / ticketsSold) * milestonePercentage : 0
  const percentOfGoal = Math.floor(progressWidth)

  // Text size classes
  const sizeClasses = {
    sm: {
      ticket: 'text-[16px] leading-[20px]',
      percent: 'text-[16px] leading-[20px]',
      milestone: 'text-[12px] leading-[16px]',
      title: 'text-[20px] leading-[26px]',
    },
    md: {
      ticket: 'text-[16px] leading-[1.375rem] md:leading-[24px]',
      percent: 'text-[16px] leading-[1.375rem] md:leading-[24px]',
      milestone: 'text-[14px] leading-[20px]',
      title: 'text-[20px] leading-[26px]',
    },
    lg: {
      ticket: 'text-[20px] leading-[26px]',
      percent: 'text-[20px] leading-[26px]',
      milestone: 'text-[16px] leading-[20px]',
      title: 'text-[24px] leading-[32px]',
    },
  }

  const classes = sizeClasses[textSize]

  return (
    <motion.div
      ref={sectionRef}
      className="relative flex w-full shrink-0 flex-col items-start gap-[12px] self-stretch"
      initial={shouldAnimateOnScroll ? { opacity: 0, y: 20 } : false}
      whileInView={shouldAnimateOnScroll ? { opacity: 1, y: 0 } : {}}
      viewport={shouldAnimateOnScroll ? { once: true, amount: 0.3 } : undefined}
      transition={shouldAnimateOnScroll ? { duration: 0.5, ease: 'easeOut' } : undefined}
    >
      {/* Goal Title (optional) */}
      {showGoalLineTitle && goalLineTitle && (
        <div className="font-whitney-semibold relative flex w-[min-content] min-w-full shrink-0 flex-col justify-center text-[24px] not-italic leading-[0] tracking-[-0.5px]">
          <p className={classes.title} style={{ color: textColor }}>
            {goalLineTitle}
          </p>
        </div>
      )}

      {/* Progress Copy */}
      <div
        className="relative flex w-full shrink-0 content-stretch items-start justify-between text-nowrap not-italic leading-[0]"
        style={{ color: textColor }}
      >
        <div className="font-whitney-bold relative flex shrink-0 flex-col justify-center">
          <p className={`whitespace-pre text-nowrap font-bold ${classes.ticket}`}>
            {ticketCount.toLocaleString()} {t('tickets', 'Tickets')}
          </p>
        </div>
        <div className="font-whitney-medium relative flex shrink-0 flex-col justify-center text-right">
          <p className={`whitespace-pre text-nowrap ${classes.percent}`}>
            {percentOfGoal}% {isLastMilestone ? t('ofGoal', 'of Goal') : t('ofGoalLineMilestone', 'of Milestone')}
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="relative flex w-full shrink-0 flex-col content-stretch items-start gap-[8px]">
        <div
          className={`relative h-[12px] w-full shrink-0 rounded-[12px] ${progressBarBg} border ${progressBarBorder}`}
        >
          <div
            className={`absolute left-0 top-0 h-full rounded-[12px] ${progressBarFillBg} transition-all duration-1000 ease-out`}
            style={{ width: `${progressWidth}%` }}
          />
        </div>
      </div>

      {/* Milestone */}
      <div className="font-whitney-medium-italic relative flex shrink-0 flex-col justify-center text-nowrap italic leading-[0]">
        <p className={`whitespace-pre ${classes.milestone}`} style={{ color: textColor }}>
          {t('ofMilestonePre', 'of')} {currentMilestone.toLocaleString()}{' '}
          {isLastMilestone ? t('totalTicketsGoalV6', 'Goal') : t('goalLineMilestoneText', 'Milestone')}
        </p>
      </div>
    </motion.div>
  )
}
